import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { GridColumnMenu } from "@mui/x-data-grid";
import { Button, Stack } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import ConfirmationDialog from "Components/Common/ConfirmationDialog";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Modal from "Components/Common/Modal";
import Backdrop from "Components/Common/Backdrop";

import { formatUtilityAddress } from "utils/formatUtilityAddress";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { generateFormData, getImageUrl } from "utils";

import {
  useLazyGetPendingDemandEstimateApprovalApplicationsData,
  useLazyGetPendingDemandEstimateApprovalForOtherUtilityApplicationsData,
  usePutDemandEstimationDocumentApproveMutation,
} from "apis/DemandEstimation";

import { StyledGrid, StyledIconButton } from "./style";

const CustomColumnMenu = (props) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuColumnsItem: null,
      columnMenuSortItem: null,
    }}
  />
);

const DemandEstimateActionModal = ({ t, handleClose, isPowerUtility }) => {
  const [
    getDemandEstimationData,
    {
      isLoading: isApplicationDataLoading,
      data: { data: pendingDemandEstimateApprovalApplicationsData } = {},
    },
  ] = useLazyGetPendingDemandEstimateApprovalApplicationsData();

  const [
    getDemandEstimationDataForOtherUtility,
    {
      isLoading: isOtherUtilityApplicationDataLoading,
      data: {
        data: pendingDemandEstimateApprovalForOtherUtilityApplicationsData,
      } = {},
    },
  ] = useLazyGetPendingDemandEstimateApprovalForOtherUtilityApplicationsData();

  const [approveDemandEstimates, { isLoading }] =
    usePutDemandEstimationDocumentApproveMutation();

  const [selectedDemandEstimateIds, setSelectedDemandEstimateIds] = useState(
    []
  );
  const [isApproveButtonClicked, setIsApproveButtonClicked] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const columns = useMemo(() => {
    return [
      {
        headerName: t("Application Number"),
        field: "applicationNumber",
        width: 150,
      },
      {
        headerName: t("Application Type"),
        field: "applicationType",
        width: 200,
        valueGetter: ({ row: { applicationType: { name } = {} } = {} }) => name,
      },
      {
        headerName: t("Name"),
        field: "name",
        width: 150,
        valueGetter: ({ row: { name } = {} }) => name,
      },
      {
        headerName: t("Utility Address"),
        field: "utilityAddress",
        width: 250,
        valueGetter: ({ row: { utilityAddress } = {} }) => {
          const address = formatUtilityAddress(utilityAddress);
          if (address) {
            return `${address}, Gandhinagar-382050`;
          }
          return "--";
        },
      },
      {
        headerName: t("Date"),
        field: "date",
        width: 150,
        valueGetter: ({ row: { submissionDate } = {} }) =>
          moment(submissionDate).isValid()
            ? convertUTCDateToLocalDate(submissionDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        headerName: t("Status"),
        field: "status",
        width: 200,
        valueGetter: ({ row: { applicationStatus: { name } = {} } }) => name,
      },
      {
        headerName: t("Action"),
        field: "action",
        width: 150,
        renderCell: ({ row: { demandEstimateUrl } = {} }) => {
          return (
            <StyledIconButton
              onClick={() => {
                setDocumentToPreview({
                  url: getImageUrl(demandEstimateUrl),
                  documentTypeName: "Demand Estimate Document",
                  documentType: "pdf",
                  documentName: "demandEstimate",
                });
              }}
            >
              <VisibilityIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </StyledIconButton>
          );
        },
      },
    ];
  }, []);

  const pendingDemandEstimateApplications = useMemo(
    () =>
      pendingDemandEstimateApprovalApplicationsData ||
      pendingDemandEstimateApprovalForOtherUtilityApplicationsData ||
      [],

    [
      pendingDemandEstimateApprovalForOtherUtilityApplicationsData,
      pendingDemandEstimateApprovalApplicationsData,
    ]
  );

  useEffect(() => {
    (async () => {
      try {
        if (isPowerUtility) {
          await getDemandEstimationData({
            isPendingDemandEstimateApprovals: true,
          });
        } else {
          await getDemandEstimationDataForOtherUtility({
            isPendingDemandEstimateApprovals: true,
          });
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [isPowerUtility]);

  const submitHandlerForApproval = async () => {
    try {
      const formData = new FormData();
      generateFormData(formData, {
        applicationIds: selectedDemandEstimateIds,
        isApproved: true,
      });
      await approveDemandEstimates(formData);
      setIsApproveButtonClicked(false);
      setSelectedDemandEstimateIds([]);
    } catch (err) {}
  };

  return (
    <>
      <Backdrop
        open={isApplicationDataLoading || isOtherUtilityApplicationDataLoading}
      />

      {isApproveButtonClicked && (
        <ConfirmationDialog
          message="Are you sure you want to approve selected demand estimates?"
          onCancel={() => setIsApproveButtonClicked(false)}
          onConfirm={submitHandlerForApproval}
          isOpen={isApproveButtonClicked}
          title="Warning"
          variant="warning"
          isLoading={isLoading}
        />
      )}

      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <Modal
        isOpen
        handleClose={handleClose}
        title={t("Demand Estimate Approval")}
        maxWidth="lg"
      >
        <StyledGrid
          className="mt-2"
          checkboxSelection
          autoHeight
          disableRowSelectionOnClick
          rows={pendingDemandEstimateApplications || []}
          columns={columns}
          pageSizeOptions={[10, 50, 100]}
          rowCount={pendingDemandEstimateApplications?.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.id}
          onRowSelectionModelChange={(ids) => {
            // eslint-disable-next-line no-undef
            const selectedIDs = new Set(ids);
            setSelectedDemandEstimateIds(Array.from(selectedIDs));
          }}
          slots={{
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t("No records found")}.
              </Stack>
            ),
          }}
          localeText={{
            footerRowSelected: () => {
              const selectedDemandEstimateIdsLength =
                selectedDemandEstimateIds.length;
              if (selectedDemandEstimateIdsLength) {
                return `${selectedDemandEstimateIdsLength} ${
                  selectedDemandEstimateIdsLength === 1 ? "row" : "rows"
                } selected`;
              }
            },
          }}
          rowSelection
          rowSelectionModel={selectedDemandEstimateIds}
          keepNonExistentRowsSelected
        />

        <div className="text-end">
          <Button
            variant="contained"
            className="mt-2"
            disabled={!selectedDemandEstimateIds.length}
            sx={{ mr: 2 }}
            color="success"
            onClick={() => setIsApproveButtonClicked(true)}
          >
            {t("Approve")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

DemandEstimateActionModal.propTypes = {
  handleClose: PropTypes.func,
  isPowerUtility: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(DemandEstimateActionModal);
